export class SystemForm {
    constructor(alpineInstance) {
        this.store = alpineInstance;

        const reservedNames = Object.getOwnPropertyNames(SystemForm.prototype)
            .filter(name => name !== 'constructor');

        const fieldList = this.store.getFields?.() || [];

        for (const fieldName of fieldList) {
            // If the name exists as a method, skip the proxy so functions like addErrorMessage still work
            if (typeof this[fieldName] === "function") {
                console.warn(`Skipping field "${fieldName}" because it conflicts with a method.`);
                continue;
            }

            this[fieldName] = this.#createFieldProxy(fieldName);
        }
    }

    #createFieldProxy(fieldName) {
        return {
            setValue: (value) => this.setValue(fieldName, value),
            getValue: () => this.getValue(fieldName),
            getInitialValue: () => this.getInitialValue(fieldName),
            getDefaultValue: () => this.getDefaultValue(fieldName),
            getDisplayValue: () => this.getDisplayValue(fieldName),
            setInitialValue: (value) => this.setInitialValue(fieldName, value),
            setDisplayValue: (value) => this.setDisplayValue(fieldName, value),
            clearValue: () => this.clearValue(fieldName),
            setReadOnly: (readOnly) => this.setReadOnly(fieldName, readOnly),
            setMandatory: (mandatory) => this.setMandatory(fieldName, mandatory),
            setDisplay: (visible) => this.setDisplay(fieldName, visible),
            isVisible: () => this.isVisible(fieldName),
            isMandatory: () => this.isMandatory(fieldName),
            isReadOnly: () => this.isReadOnly(fieldName),
            canRead: () => this.canRead(fieldName),
            canWrite: () => this.canWrite(fieldName)
        };
    }

    getUniqueValue() {
        return this.store.getUniqueValue();
    }
    _getUniqueValue() {
        return this.getUniqueValue();
    }

    isNewRecord() {
        return this.store.isNewRecord();
    }
    _isNewRecord() {
        return this.isNewRecord();
    }

    getValue(fieldName) {
        return this.store[fieldName]?.value ?? null;
    }
    _getValue(fieldName) {
        return this.getValue(fieldName);
    }

    getInitialValue(fieldName) {
        return this.store[fieldName]?.initialValue ?? null;
    }
    _getInitialValue(fieldName) {
        return this.getInitialValue(fieldName);
    }

    getDefaultValue(fieldName) {
        return this.store[fieldName]?.defaultValue ?? null;
    }
    _getDefaultValue(fieldName) {
        return this.getDefaultValue(fieldName);
    }

    getDisplayValue(fieldName) {
        return this.store.getDisplayValue(fieldName) ?? this.getValue(fieldName);
    }
    _getDisplayValue(fieldName) {
        return this.getDisplayValue(fieldName);
    }

    setValue(fieldName, value) {
        if (this.store[fieldName] && !this.store[fieldName].readOnly) {
            this.store.setValue(fieldName, value);
        } else {
            console.warn(`Field ${fieldName} is either read-only or does not exist.`);
        }
    }
    _setValue(fieldName, value) {
        return this.setValue(fieldName, value);
    }

    setInitialValue(fieldName, value) {
        if (this.store[fieldName]) {
            this.store[fieldName].setInitialValue(value);
        } else {
            console.warn(`Field ${fieldName} does not exist.`);
        }
    }
    _setInitialValue(fieldName, value) {
        return this.setInitialValue(fieldName, value);
    }

    setDisplayValue(fieldName, value) {
        if (this.store[fieldName]) {
            this.store[fieldName].setDisplayValue(value);
        } else {
            console.warn(`Field ${fieldName} does not exist.`);
        }
    }
    _setDisplayValue(fieldName, value) {
        return this.setDisplayValue(fieldName, value);
    }

    clearValue(fieldName) {
        this.setValue(fieldName, null);
    }
    _clearValue(fieldName) {
        return this.clearValue(fieldName);
    }

    setReadOnly(fieldName, readOnly) {
        if (this.store[fieldName]) {
            this.store.setReadOnly(fieldName, readOnly);
        } else {
            console.warn(`Field ${fieldName} does not exist.`);
        }
    }
    _setReadOnly(fieldName, readOnly) {
        return this.setReadOnly(fieldName, readOnly);
    }

    setMandatory(fieldName, mandatory) {
        if (this.store[fieldName]) {
            this.store.setMandatory(fieldName, mandatory);
        } else {
            console.warn(`Field ${fieldName} does not exist.`);
        }
    }
    _setMandatory(fieldName, mandatory) {
        return this.setMandatory(fieldName, mandatory);
    }

    setDisplay(fieldName, visible) {
        if (this.store[fieldName]) {
            this.store.setDisplay(fieldName, visible);
        } else {
            console.warn(`Field ${fieldName} does not exist.`);
        }
    }
    _setDisplay(fieldName, visible) {
        return this.setDisplay(fieldName, visible);
    }

    isVisible(fieldName) {
        return this.store.isVisible?.(fieldName) ?? true;
    }
    _isVisible(fieldName) {
        return this.isVisible(fieldName);
    }

    isMandatory(fieldName) {
        return this.store.isMandatory?.(fieldName) ?? false;
    }
    _isMandatory(fieldName) {
        return this.isMandatory(fieldName);
    }

    isReadOnly(fieldName) {
        return this.store.isReadOnly?.(fieldName) ?? false;
    }
    _isReadOnly(fieldName) {
        return this.isReadOnly(fieldName);
    }

    canRead(fieldName) {
        return this.store[fieldName]?.canRead ?? false;
    }
    _canRead(fieldName) {
        return this.canRead(fieldName);
    }

    canWrite(fieldName) {
        return this.store[fieldName]?.canWrite ?? false;
    }
    _canWrite(fieldName) {
        return this.canWrite(fieldName);
    }

    checkMandatory() {
        let valid = true;
        const fieldList = this.store.getFields?.() || [];
        fieldList.forEach(fieldName => {
            const field = this.store[fieldName];
            if (field?.mandatory && field?.visible && (field.value === null || field.value === "")) {
                console.warn(`Field ${fieldName} is mandatory and must not be empty.`);
                valid = false;
            }
        });
        return valid;
    }
    
    _checkMandatory() {
        return this.checkMandatory();
    }

    isDirty() {
        return this.store.isDirty();
    }
    _isDirty() {
        return this.isDirty();
    }

    toJSON() {
        return JSON.stringify({
            fields: this.store.getFieldValues()
        });
    }
    _toJSON() {
        return this.toJSON();
    }

    save() {
        if (!this.checkMandatory()) {
            console.error("Mandatory fields are not filled");
            return;
        }

        const payload = {
            formData: this.store.getFieldValues(),
            additionalData: {
                currentURL: window.location.href
            }
        };

        fetch(this.store.submitUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        }).then(response => response.json())
            .then(data => {
                if (data.redirectUrl) {
                    window.location.href = data.redirectUrl;
                } else {
                    console.log('Form saved successfully:', data);
                }
            }).catch(error => console.error('Error during save:', error));
    }
    _save() {
        return this.save();
    }

    handleServerAction(actionSysId) {
        this.store.handleServerAction(actionSysId);
    }
    _handleServerAction(actionSysId) {
        return this.handleServerAction(actionSysId);
    }

    addClientAction(sysId, fn) {
        this.store.clientActions[sysId] = fn;
    }
    _addClientAction(sysId, fn) {
        this.addClientAction(sysId, fn);
    }

    handleClientAction(actionSysId) {
        this.store.handleClientAction(actionSysId);
    }
    _handleClientAction(actionSysId) {
        return this.handleClientAction(actionSysId);
    }

    addInfoMessage(message, seconds = 7) {
        this.store.addInfoMessage(message, seconds);
    }
    _addInfoMessage(message, seconds = 7) {
        return this.addInfoMessage(message, seconds);
    }

    addErrorMessage(message, seconds = 7) {
        this.store.addErrorMessage(message, seconds);
    }
    _addErrorMessage(message, seconds = 7) {
        return this.addErrorMessage(message, seconds);
    }

    addSuccessMessage(message, seconds = 7) {
        this.store.addSuccessMessage(message, seconds);
    }
    _addSuccessMessage(message, seconds = 7) {
        return this.addSuccessMessage(message, seconds);
    }

    addWarningMessage(message, seconds = 7) {
        this.store.addWarningMessage(message, seconds);
    }
    _addWarningMessage(message, seconds = 7) {
        return this.addWarningMessage(message, seconds);
    }
}
